import axios from 'axios';

// Create axios instance with base configuration
// Uses environment variable REACT_APP_API_URL (configured in .env)
// Defaults to localhost if not set
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api-gateway.studentsherald.com/bo/api',
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message || error.response?.data?.error || '';

    // Handle 401 Unauthorized or 403 with token errors
    if (status === 401 || (status === 403 && message.toLowerCase().includes('token'))) {
      const isTokenError =
        message.toLowerCase().includes('token') ||
        message.toLowerCase().includes('expired') ||
        message.toLowerCase().includes('jwt') ||
        message.toLowerCase().includes('unauthorized') ||
        message.toLowerCase().includes('access denied') ||
        message.includes('Access token required');

      // Don't redirect on login page 401 (invalid credentials)
      const isLoginPage = window.location.pathname.includes('/login');

      if ((isTokenError || status === 401) && !isLoginPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// Authentication API (Unified - All users in operator table)
// ============================================
export const authAPI = {
  // Login - works for all users (admin, content manager, editor, writer, viewer)
  login: (credentials) => api.post('/auth/login', credentials),
  // Register new user
  register: (userData) => api.post('/auth/register', userData),
  // Get current user profile
  getProfile: () => api.get('/auth/me'),
  // Logout
  logout: () => api.get('/auth/logout'),
  // Get all available roles
  getRoles: () => api.get('/auth/roles'),
  // Get all permissions
  getPermissions: () => api.get('/auth/permissions'),
};

// ============================================
// User Management API (Unified - All users in operator table)
// All users including admins are stored in operator table
// User type is determined by role (ADMIN, CONTENT_MANAGER, EDITOR, etc.)
// ============================================
export const usersAPI = {
  // Get all users (all operators regardless of role)
  getAll: (params) => api.get('/admin/users/all', { params }),
  // Get user by ID
  getById: (id) => api.get(`/admin/users/${id}`),
  // Create new user (operator with specified role)
  create: (data) => api.post('/admin/users', data),
  // Update user
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  // Delete/deactivate user
  delete: (id) => api.delete(`/admin/users/${id}`),
  // Change password
  changePassword: (id, data) => api.post(`/admin/users/${id}/change-password`, data),
  // Get roles for dropdown
  getRoles: () => api.get('/auth/roles'),
  // Filter users by role
  getByRole: (roleCode, params) => api.get(`/admin/users/by-role/${roleCode}`, { params }),
};

// ============================================
// Category API
// ============================================
export const categoryAPI = {
  getAll: (params) => {
    const queryParams = {
      ...params,
      limit: params?.size || params?.limit || 100,
      page: params?.page || 1
    };
    delete queryParams.size;
    return api.get('/categories/admin/all', { params: queryParams });
  },
  getDropdown: () => api.get('/categories/dropdown'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories/admin/create', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/categories/admin/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/categories/admin/${id}`),
  search: (query) => api.get(`/categories/search?query=${query}`),
};

// ============================================
// Article API
// ============================================
export const articleAPI = {
  getAll: (params) => {
    const queryParams = { ...params, size: params?.size || 100, page: params?.page || 1 };
    return api.get('/articles/admin/all', { params: queryParams });
  },
  getById: (id) => api.get(`/articles/${id}`),
  getPopular: () => api.get('/articles?label=popular'),
  getRecent: () => api.get('/articles?label=recent'),
  getFeatured: () => api.get('/articles?label=featured'),
  create: (data) => api.post('/articles/admin/create', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/articles/admin/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/articles/admin/${id}`),
  bulkAction: (data) => api.post('/articles/admin/bulk-action', data),
};

// ============================================
// Hero Content API
// ============================================
export const heroContentAPI = {
  getAll: (params) => api.get('/hero-content/admin/all', { params }),
  getById: (id) => api.get(`/hero-content/${id}`),
  create: (data) => api.post('/hero-content', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/hero-content/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/hero-content/${id}`),
};

// ============================================
// Carousel API
// ============================================
export const carouselAPI = {
  getAll: (params) => api.get('/carousel/admin/all', { params }),
  getById: (id) => api.get(`/carousel/${id}`),
  create: (data) => api.post('/carousel', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/carousel/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/carousel/${id}`),
};

// ============================================
// Course API (Enhanced with Dynamic Relationships)
// ============================================
export const courseAPI = {
  // Get all courses with optional relationships
  getAll: (params) => {
    const queryParams = { 
      ...params, 
      limit: params?.size || params?.limit || 100, 
      page: params?.page || 1,
      withRelationships: params?.withRelationships !== false ? 'true' : 'false',
      category: params?.category,
      level: params?.level,
      status: params?.status,
      university: params?.university,
      search: params?.search
    };
    delete queryParams.size;
    return api.get('/courses/admin/all', { params: queryParams });
  },
  // Get single course with full relationships
  getById: (id, withRelationships = true) => {
    return api.get(`/courses/${id}`, { 
      params: { withRelationships: withRelationships ? 'true' : 'false' } 
    });
  },
  // Search courses
  search: (query) => api.get(`/courses?search=${query}`),
  // Create course
  create: (data) => api.post('/courses/admin/create', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Update course
  update: (id, data) => api.put(`/courses/admin/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Delete course
  delete: (id) => api.delete(`/courses/admin/${id}`),
  // Get courses by category
  getByCategory: (categoryId, params) => {
    const queryParams = { ...params, category: categoryId };
    return api.get('/courses', { params: queryParams });
  },
  // Get courses by university
  getByUniversity: (universityId, params) => {
    const queryParams = { ...params, university: universityId };
    return api.get('/courses', { params: queryParams });
  },
  // Get courses by level
  getByLevel: (level, params) => {
    const queryParams = { ...params, level };
    return api.get('/courses', { params: queryParams });
  },
};

// ============================================
// University API (Enhanced with Dynamic Relationships)
// ============================================
export const universityAPI = {
  // Get all universities with optional courses and relationships
  getAll: (params) => {
    const queryParams = { 
      ...params, 
      limit: params?.size || params?.limit || 100, 
      page: params?.page || 1,
      withCourses: params?.withCourses || 'false', // Include courses with category relationships
      country: params?.country, // Filter by country
      search: params?.search // Search query
    };
    delete queryParams.size;
    return api.get('/universities/admin/all', { params: queryParams });
  },
  // Get single university with full course details and relationships
  getById: (id, includeCourses = true) => {
    return api.get(`/universities/${id}`, { 
      params: { includeCourses: includeCourses ? 'true' : 'false' } 
    });
  },
  // Search universities
  search: (query) => api.get(`/universities?search=${query}`),
  // Create university
  create: (data) => api.post('/universities/admin/create', data),
  // Update university
  update: (id, data) => api.put(`/universities/admin/${id}`, data),
  // Delete university
  delete: (id) => api.delete(`/universities/admin/${id}`),
  // Get universities by category (filter by course category)
  getByCategory: (categoryId, params) => {
    const queryParams = { ...params, category: categoryId };
    return api.get('/universities', { params: queryParams });
  },
  // Get universities by country
  getByCountry: (country, params) => {
    const queryParams = { ...params, country };
    return api.get('/universities', { params: queryParams });
  },
};

// ============================================
// Tag API
// ============================================
export const tagAPI = {
  getAll: (params) => api.get('/tags', { params }),
  getById: (id) => api.get(`/tags/${id}`),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  getPopular: (limit = 10) => api.get(`/tags/popular?limit=${limit}`),
  getSuggestions: (query) => api.get(`/tags/suggestions?q=${query}`),
};

// ============================================
// Analytics API
// ============================================
export const analyticsAPI = {
  getDashboard: () => api.get('/admin/analytics/dashboard'),
  getUsers: () => api.get('/admin/analytics/users'),
  getUserActivity: () => api.get('/admin/analytics/user-activity/devices'),
  getGeography: () => api.get('/admin/analytics/user-activity/geography'),
  getContent: (params) => api.get('/admin/analytics/content', { params }),
  getDaily: () => api.get('/admin/analytics/daily'),
  getWeekly: () => api.get('/admin/analytics/weekly'),
  getMonthly: () => api.get('/admin/analytics/monthly'),
  getDeviceAnalytics: () => api.get('/admin/analytics/user-activity/devices'),
  getGeographicAnalytics: () => api.get('/admin/analytics/user-activity/geography'),
  getRealTimeStats: () => api.get('/admin/analytics/system/realtime'),
  getPerformanceMetrics: () => api.get('/admin/analytics/performance'),
  getSystemAnalytics: () => api.get('/admin/analytics/system'),
};

// ============================================
// SEO Analytics API
// ============================================
export const seoAnalyticsAPI = {
  getOverview: (params) => api.get('/admin/analytics/seo/overview', { params }),
  getKeywords: (params) => api.get('/admin/analytics/seo/keywords', { params }),
  getPages: (params) => api.get('/admin/analytics/seo/pages', { params }),
  getVitals: () => api.get('/admin/analytics/seo/vitals'),
};

// ============================================
// Ad Analytics API
// ============================================
export const adAnalyticsAPI = {
  getOverview: (params) => api.get('/advertisements/analytics/overview', { params }),
  getBySize: () => api.get('/advertisements/analytics/by-size'),
  getByBanner: () => api.get('/advertisements/analytics/by-banner'),
  getByDevice: () => api.get('/advertisements/analytics/by-device'),
  getByPosition: () => api.get('/advertisements/analytics/by-position'),
  getConfig: () => api.get('/advertisements/config/options'),
};

// ============================================
// Reports API
// ============================================
export const reportsAPI = {
  getUserReports: (params) => api.get('/reports/users', { params }),
  getContentReports: (params) => api.get('/reports/content', { params }),
};

// ============================================
// Settings API
// ============================================
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// ============================================
// Upload API
// ============================================
export const uploadAPI = {
  upload: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadImage: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadDocument: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadMultiple: (formData) => api.post('/upload/multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getFiles: (params) => api.get('/upload/files', { params }),
  deleteFile: (id) => api.delete(`/upload/files/${id}`),
};

// ============================================
// Contact API
// ============================================
export const contactAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  submit: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.put(`/contacts/${id}/delete`), // Changed to PUT to match backend
};

// ============================================
// System API
// ============================================
export const systemAPI = {
  getHealth: () => api.get('/health'),
  getActiveStatus: () => api.get('/system-data/active-status'),
  getStates: () => api.get('/geographic/states'),
  getCities: () => api.get('/geographic/cities'),
  getCountries: () => api.get('/geographic/countries'),
  getPinCodes: () => api.get('/geographic/pincodes'),
  getSystemData: () => api.get('/system-data'),
};

// ============================================
// Business API
// ============================================
export const businessAPI = {
  getPartners: () => api.get('/business-entities/partners'),
};

// ============================================
// Document API
// ============================================
export const documentAPI = {
  getAll: (params) => api.get('/admin/documents', { params }),
  getById: (id) => api.get(`/admin/documents/${id}`),
  create: (data) => api.post('/admin/documents', data),
  update: (id, data) => api.put(`/admin/documents/${id}`, data),
  delete: (id) => api.put(`/admin/documents/${id}/delete`), // Changed to PUT for soft delete
  search: (query) => api.get(`/admin/documents?search=${query}`),
  getStats: () => api.get('/admin/documents/stats'),
  getCategories: () => api.get('/admin/documents/categories/all'),
  getTypes: () => api.get('/admin/documents/types/all'),
  bulkAction: (data) => api.post('/admin/documents/bulk-action', data),
};

// ============================================
// Dashboard API
// ============================================
export const dashboardAPI = {
  getData: () => api.get('/dashboard'),
  getStats: () => api.get('/dashboard/stats'),
};

export default api;
